import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { getEventBasePath, getFrontendDomain } from "@/lib/auth/session";

// ─── HTTP Link ────────────────────────────────────────────────────
const graphqlUrl =
  import.meta.env.VITE_GRAPHQL_URL ?? "http://localhost:4000/graphql";
const isLocalApi = ["localhost", "127.0.0.1", "[::1]"].includes(
  new URL(graphqlUrl, window.location.origin).hostname,
);
const httpLink = new HttpLink({
  uri: import.meta.env.DEV && isLocalApi ? "/api/graphql" : graphqlUrl,
  // Credentials for cookie-based auth
  credentials: "include",
});

// ─── Auth Link — scopes cookie sessions to this event ─────────────
const authLink = new ApolloLink((operation, forward) => {
  operation.setContext(
    ({ headers = {} }: { headers: Record<string, string> }) => ({
      headers: {
        "X-Frontend-Domain": getFrontendDomain(),
        ...(getEventBasePath() !== "/"
          ? { "X-Event-Slug": getEventBasePath().slice(1) }
          : {}),
        ...headers,
      },
    }),
  );

  return forward(operation);
});

// ─── Error Link ───────────────────────────────────────────────────
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(
        `[GraphQL error]: Operation: ${operation.operationName}, Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${String(path)}`,
      );

      // Handle auth errors — redirect to login
      if (extensions?.["code"] === "UNAUTHENTICATED") {
        localStorage.removeItem("fd:auth_token");
        localStorage.removeItem("fd:refresh_token");
        const basePath = getEventBasePath();
        const loginPath = `${basePath === "/" ? "" : basePath}/auth/login`;
        if (window.location.pathname !== loginPath) {
          window.location.href = loginPath;
        }
      }
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError.message}`);
  }
});

// ─── Retry Link — auto-retry transient network failures ───────────
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 3000,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error) => !!error && error.statusCode !== 401,
  },
});

// ─── In-Memory Cache ─────────────────────────────────────────────
// Configure type policies for normalized caching
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // Paginated events list — merge pages into single array
        events: {
          keyArgs: ["filter", "search"],
          merge(existing = { nodes: [] }, incoming) {
            return {
              ...incoming,
              nodes: [...(existing.nodes ?? []), ...(incoming.nodes ?? [])],
            };
          },
        },
      },
    },
    // Unique ID fields for cache normalization
    Event: {
      keyFields: ["id"],
    },
    User: {
      keyFields: ["id"],
    },
    Company: {
      keyFields: ["id"],
    },
    Team: {
      keyFields: ["id"],
    },
  },
});

// ─── Apollo Client singleton ──────────────────────────────────────
export const apolloClient = new ApolloClient({
  link: from([errorLink, retryLink, authLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      // Serve from cache immediately, then update from network
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "cache-first",
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
  connectToDevTools: import.meta.env.DEV,
  name: "football-dashboard",
  version: "1.0",
});
